import React, { createContext, useContext, useState, useEffect } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import Cookies from 'js-cookie';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(Cookies.get("google_access_token") || null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Check token expiry and set it
  const checkAndSetTokenExpiry = async (token) => {
    if (!token) {
      setTokenExpiry(null);
      return false;
    }
    try {
      const response = await fetch(`https://oauth2.googleapis.com/tokeninfo?access_token=${token}`);
      const data = await response.json();

      if (data.error) {
        console.log("Token invalid or expired:", data.error_description);
        setTokenExpiry(null);
        return false;
      }

      // Set expiry time (convert to milliseconds and subtract 5 minutes for safety margin)
      const expiryTime = Date.now() + (parseInt(data.exp) * 1000) - (5 * 60 * 1000);
      setTokenExpiry(expiryTime);
      return true;
    } catch (error) {
      console.error("Error checking token:", error);
      setTokenExpiry(null);
      return false;
    }
  };

  // Load token from cookies and set expiry on app start
  useEffect(() => {
    const storedToken = Cookies.get("google_access_token");
    if (storedToken) {
      setAccessToken(storedToken);
      checkAndSetTokenExpiry(storedToken);
    }
  }, []);

  // Silent token refresh function
  const silentRefresh = async () => {
    if (refreshing) return;

    try {
      setRefreshing(true);
      const refreshToken = Cookies.get('google_refresh_token');

      if (!refreshToken) {
        console.error("No refresh token available for silent refresh.");
        // Optionally, handle the case where the user might need to re-login
        return false;
      }

      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
          refresh_token: refreshToken,
          grant_type: 'refresh_token',
        }),
      });

      const data = await response.json();

      if (data.access_token) {
        console.log("✅ Token refreshed successfully!");
        setAccessToken(data.access_token);
        Cookies.set('google_access_token', data.access_token, { sameSite: 'Lax', secure: true });
        await checkAndSetTokenExpiry(data.access_token);
        return true;
      } else {
        console.error("Failed to refresh token:", data);
        // Optionally, handle the failure (e.g., clear tokens, force re-login)
        return false;
      }
    } catch (error) {
      console.error("Error in silent refresh:", error);
      return false;
    } finally {
      setRefreshing(false);
    }
  };

  // Auto refresh mechanism
  useEffect(() => {
    if (!tokenExpiry || !accessToken) return;

    const refreshInterval = setInterval(async () => {
      const timeUntilExpiry = tokenExpiry - Date.now();

      // If token is about to expire (less than 5 minutes) and not already refreshing
      if (timeUntilExpiry < 5 * 60 * 1000 && !refreshing) {
        console.log("⏳ Token nearing expiration, attempting silent refresh...");
        await silentRefresh();
      }
    }, 60000); // Check every minute

    return () => clearInterval(refreshInterval);
  }, [tokenExpiry, accessToken, refreshing, silentRefresh]); // Ensure silentRefresh is in the dependency array

  // Function to update token and store in cookies
  const updateAccessToken = async (token) => {
    setAccessToken(token);
    Cookies.set('google_access_token', token, { sameSite: 'Lax', secure: true });
    await checkAndSetTokenExpiry(token);
  };

  return (
    <AuthContext.Provider value={{
      accessToken,
      setAccessToken: updateAccessToken,
      silentRefresh,
      tokenExpiry
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}